import { useIsDemo } from "@/demo/DemoContext";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useCallback, useEffect, useState } from "react";
import {
  CircleCheck,
  CreditCard,
  History,
  Link2,
  Plus,
  ReceiptText,
  RefreshCw,
  Settings2,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { CreateLedgerAccountDialog, isSpendAccount } from "@/components/CreateLedgerAccountDialog";
import { apiErrorMessage } from "@/lib/api-error";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { cardLabel } from "@/components/ExpensesTable";
import { EmptyState, LoadingState } from "@/components/ExpenseUi";
import { formatDate } from "@/lib/format";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { components } from "@dolfin/api-public";
import { LinkCardDialog } from "@/components/LinkCardDialog";
import { OrgMemberSelect, memberLabel } from "@/components/OrgMemberSelect";
import { usePermissions } from "@/lib/permissions";
import { SimulateTransactionDialog } from "@/components/SimulateTransactionDialog";

type CardConnectionResponse = components["schemas"]["CardConnectionResponse"];
type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];

// An unconfigured clearing account means expenses that never post, so it is called out rather than failing quietly.
export interface CardConnectionsPageProps {
  // Opens an expense — used to land on the one a simulated purchase just created.
  onSelectExpense?: (expenseId: string) => void;
  // Opens the expenses list filtered to one card.
  onViewSpend?: (cardConnectionId: string) => void;
}

export function CardConnectionsPage({ onSelectExpense, onViewSpend }: CardConnectionsPageProps = {}) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canAssignHolders = can("assignSpenders");
  const isDemo = useIsDemo();
  const [cards, setCards] = useState<CardConnectionResponse[]>([]);
  const [accounts, setAccounts] = useState<LedgerAccountResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [simulating, setSimulating] = useState<CardConnectionResponse | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creatingDefaultAccount, setCreatingDefaultAccount] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [cardsResult, accountsResult] = await Promise.all([
      api.GET("/v1/card-connections", { params: { query: {} } }),
      api.GET("/v1/accounting/ledger-accounts", { params: { query: {} } }),
    ]);
    setCards(cardsResult.data ?? []);
    setAccounts(accountsResult.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  async function syncCards() {
    setBusy(true);
    const { error } = await api.POST("/v1/card-connections/sync", {});
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't sync cards."));
    } else {
      toast.success("Cards synced.");
      await load();
    }
    setBusy(false);
  }

  async function importHistory() {
    setBusy(true);
    const { data, error } = await api.POST("/v1/card-connections/backfill", { body: {} });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't import card history."));
    } else if (data?.transactionsImported === 0) {
      toast.success("Nothing new to import - your transactions are already up to date.");
    } else {
      toast.success(
        `Imported ${data?.transactionsImported} transaction${data?.transactionsImported === 1 ? "" : "s"}.` +
          // Never silently truncate: a user told "imported" when we stopped early has no way to know.
          (data?.truncated ? " There was more history than we import in one go - run it again for older transactions." : ""),
      );
    }
    setBusy(false);
  }

  async function updateCard(
    card: CardConnectionResponse,
    patch: Partial<{
      displayName: string;
      clearingLedgerAccountId: string;
      defaultExpenseLedgerAccountId: string;
    }>,
  ) {
    const { error } = await api.PATCH("/v1/card-connections/{id}", {
      params: { path: { id: card.id! } },
      body: patch,
    });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't update the card."));
    } else {
      toast.success("Card updated.");
      await load();
    }
  }

  // Future spend on the card is the holder's; the backend also fills in the card's spend that has nobody yet.
  async function setHolder(card: CardConnectionResponse, userId: string | null) {
    const { error } = userId
      ? await api.PUT("/v1/card-connections/{id}/holder", {
          params: { path: { id: card.id! } },
          body: { userId },
        })
      : await api.DELETE("/v1/card-connections/{id}/holder", { params: { path: { id: card.id! } } });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't change the card holder."));
    } else {
      toast.success(userId ? "Card holder assigned." : "Card is now unassigned.");
      await load();
    }
  }

  const expenseAccounts = accounts.filter(isSpendAccount);
  // Xero and QuickBooks only pay card spend from a Bank or Credit Card account, and the backend refuses others.
  // An account with no platform type is still offered, as the backend allows it.
  const clearingAccounts = accounts.filter((a) =>
    a.accountType
      ? a.accountType === "bank" || a.accountType === "credit_card"
      : a.classification === "asset" || a.classification === "liability",
  );
  // Looked up by id so the sheet shows the card as it is after each save, not as it was when opened.
  const editing = cards.find((c) => c.id === editingId) ?? null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Linked cards"
        subtitle="Link the cards your team already uses. Card details are captured by the card provider, so no card number reaches Dolfin."
        actions={
          <div className="flex gap-2">
            {cards.length > 0 ? (
              <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
                <Button variant="outline" size="sm" disabled={busy} onClick={importHistory}>
                  <History className="size-4" /> Import history
                </Button>
              </PermissionGuard>
            ) : null}
            <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
              <Button variant="outline" size="sm" disabled={busy} onClick={syncCards}>
                <RefreshCw className="size-4" /> Sync
              </Button>
            </PermissionGuard>
            <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
              <Button size="sm" disabled={busy} onClick={() => setLinkOpen(true)}>
                <Link2 className="size-4" /> Link a card
              </Button>
            </PermissionGuard>
          </div>
        }
      />

      {loading ? (
        <LoadingState label="Loading cards..." />
      ) : cards.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="No cards linked yet"
          action={
            <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
              <Button size="sm" onClick={() => setLinkOpen(true)}>
                <Link2 className="size-4" /> Link a card
              </Button>
            </PermissionGuard>
          }
        >
          Link a card and its transactions start arriving straight away, each one captured and coded as an
          expense.
        </EmptyState>
      ) : (
        <div className="grid gap-x-5 gap-y-6 @2xl:grid-cols-2 @5xl:grid-cols-3">
          {cards.map((card) => (
            <div key={card.id} className="flex flex-col gap-3">
              <CardFace card={card} onOpen={() => setEditingId(card.id!)} />
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-1">
                <SetupStatus card={card} />
                <div className="-mr-2 flex items-center">
                  {onViewSpend ? (
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => onViewSpend(card.id!)}>
                      <ReceiptText className="size-4" /> Spend
                    </Button>
                  ) : null}
                  {card.provider === "Fake" ? (
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setSimulating(card)}>
                      <Plus className="size-4" /> Transaction
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label="Card settings"
                    onClick={() => setEditingId(card.id!)}
                  >
                    <Settings2 className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={editing !== null} onOpenChange={(open) => !open && setEditingId(null)}>
        <SheetContent className="w-full gap-0 sm:max-w-md">
          {editing ? (
            <>
              <SheetHeader className="border-b">
                <SheetTitle>{editing.displayName || cardLabel({ cardNetwork: editing.network, cardLast4: editing.last4 })}</SheetTitle>
                <SheetDescription>
                  {/* The title is already the card label when the card has no name - do not repeat it. */}
                  {[
                    editing.displayName ? cardLabel({ cardNetwork: editing.network, cardLast4: editing.last4 }) : null,
                    expiry(editing) ? `Expires ${expiry(editing)}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </SheetDescription>
              </SheetHeader>

              <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">
                {/* Keyed on the card so switching cards resets the uncontrolled input. */}
                <div key={editing.id} className="space-y-2">
                  <Label htmlFor="card-name">Name</Label>
                  <Input
                    id="card-name"
                    defaultValue={editing.displayName ?? ""}
                    placeholder="e.g. Ops card"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (editing.displayName ?? "")) {
                        updateCard(editing, { displayName: value });
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown beside the last four digits everywhere this card appears.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="card-holder" className="flex items-center gap-1.5">
                    Card holder
                    <InfoHint label="More about the card holder">
                      The person who uses this card. New spend on it is attributed to them, and so is earlier
                      spend that has nobody yet. Spend already attributed to someone stays with them.
                    </InfoHint>
                  </Label>
                  <OrgMemberSelect
                    id="card-holder"
                    value={editing.holder?.userId ?? null}
                    noneLabel="Unassigned (shared card)"
                    disabled={!canAssignHolders}
                    onChange={(userId) => setHolder(editing, userId)}
                  />
                  {canAssignHolders ? null : (
                    <p className="text-xs text-muted-foreground">Only an organisation admin can change the holder.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    Clearing account
                    <InfoHint label="More about the clearing account">
                      The credit side of every expense on this card: the bank or credit-card account the
                      money left. Without it the card's spend cannot post.
                    </InfoHint>
                  </Label>
                  <Select
                    value={editing.clearingLedgerAccountId ?? undefined}
                    onValueChange={(value) => updateCard(editing, { clearingLedgerAccountId: value })}
                  >
                    <SelectTrigger
                      className={cn(
                        "w-full",
                        !editing.clearingLedgerAccountId && "border-amber-500/50",
                      )}
                    >
                      <SelectValue placeholder="Choose an account" />
                    </SelectTrigger>
                    <SelectContent>
                      {clearingAccounts.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-muted-foreground">
                          No bank or credit-card accounts synced from your accounting platform. Add the card's
                          account there, then sync.
                        </div>
                      ) : null}
                      {/* A card set up before this rule keeps showing its account, marked, so the person sees
                          what to change rather than an empty picker. */}
                      {editing.clearingLedgerAccountId &&
                      !clearingAccounts.some((a) => a.id === editing.clearingLedgerAccountId) ? (
                        <SelectItem value={editing.clearingLedgerAccountId} disabled>
                          {accounts.find((a) => a.id === editing.clearingLedgerAccountId)?.name ?? "Current account"} (not a
                          bank or credit-card account)
                        </SelectItem>
                      ) : null}
                      {clearingAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id!}>
                          {account.displayId ? `${account.displayId} · ` : ""}
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    Default expense account
                    <InfoHint label="More about the default expense account">
                      Used when a merchant category has no mapping of its own, so spend in an unmapped
                      category still codes somewhere.
                    </InfoHint>
                  </Label>
                  <Select
                    value={editing.defaultExpenseLedgerAccountId ?? undefined}
                    onValueChange={(value) =>
                      value === "__create_account__"
                        ? setCreatingDefaultAccount(true)
                        : updateCard(editing, { defaultExpenseLedgerAccountId: value })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose an account" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id!}>
                          {account.displayId ? `${account.displayId} · ` : ""}
                          {account.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__create_account__" className="text-primary">
                        <Plus className="size-3.5" /> Create an account…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-y-2 border-t pt-4 text-sm">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd>
                    <CardStatus status={editing.status} />
                  </dd>
                  <dt className="text-muted-foreground">Provider</dt>
                  <dd>{editing.provider === "Fake" ? "Sample card (Sandbox)" : editing.provider}</dd>
                  <dt className="text-muted-foreground">Linked</dt>
                  <dd>{editing.enrolledAt ? formatDate(editing.enrolledAt) : "-"}</dd>
                </dl>
              </div>

              <SheetFooter className="flex-row border-t">
                {onViewSpend ? (
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => onViewSpend(editing.id!)}>
                    <ReceiptText className="size-4" /> View spend
                  </Button>
                ) : null}
                {editing.provider === "Fake" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setEditingId(null);
                      setSimulating(editing);
                    }}
                  >
                    <Plus className="size-4" /> Add a transaction
                  </Button>
                ) : null}
              </SheetFooter>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <LinkCardDialog open={linkOpen} onOpenChange={setLinkOpen} onLinked={load} />
      <SimulateTransactionDialog
        card={simulating}
        onOpenChange={(open) => !open && setSimulating(null)}
        onCreated={(transaction) => {
          if (transaction.expenseId && onSelectExpense) onSelectExpense(transaction.expenseId);
        }}
      />
      <CreateLedgerAccountDialog
        open={creatingDefaultAccount}
        onOpenChange={setCreatingDefaultAccount}
        purpose="card"
        onCreated={(account) => {
          setAccounts((current) => [...current, account]);
          const card = cards.find((c) => c.id === editingId);
          if (card) updateCard(card, { defaultExpenseLedgerAccountId: account.id! });
        }}
      />
    </PageContainer>
  );
}

function expiry(card: CardConnectionResponse) {
  if (!card.expiryMonth || !card.expiryYear) return null;
  return `${String(card.expiryMonth).padStart(2, "0")}/${String(card.expiryYear).slice(-2)}`;
}

// Neutral by design: colour on this page is kept for whether the card can post.
function CardFace({ card, onOpen }: { card: CardConnectionResponse; onOpen: () => void }) {
  const exp = expiry(card);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative flex aspect-[1.586] w-full flex-col justify-between overflow-hidden rounded-2xl border bg-gradient-to-br from-foreground/[0.09] via-foreground/[0.04] to-foreground/[0.01] p-5 text-left shadow-sm transition hover:border-foreground/25 hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn("truncate font-semibold", !card.displayName && "text-muted-foreground")}>
            {card.displayName || "Unnamed card"}
          </span>
          {card.provider === "Fake" ? (
            <span className="shrink-0 rounded-full border px-1.5 py-px text-[9px] font-semibold tracking-wider text-muted-foreground uppercase">
              Sample
            </span>
          ) : null}
        </div>
        <CardStatus status={card.status} />
      </div>

      <span aria-hidden className="h-7 w-9 rounded-md border bg-foreground/[0.06]" />

      <div>
        <div className="font-mono text-lg tracking-[0.18em] tabular-nums">
          <span className="text-muted-foreground">•••• •••• •••• </span>
          {card.last4 ?? "····"}
        </div>
        <div className="mt-2 flex items-end justify-between gap-3">
          <span className="min-w-0">
            {/* Printed where a card carries its holder's name. */}
            <span
              className={cn(
                "block truncate text-[11px] tracking-[0.14em] uppercase",
                card.holder ? "text-foreground/80" : "text-muted-foreground",
              )}
            >
              {card.holder ? memberLabel(card.holder) : "Unassigned"}
            </span>
            <span className="text-sm font-bold tracking-[0.12em] uppercase">{card.network ?? "Card"}</span>
          </span>
          {exp ? (
            <span className="text-right">
              <span className="block text-[9px] tracking-[0.14em] text-muted-foreground uppercase">Expires</span>
              <span className="font-mono text-sm tabular-nums">{exp}</span>
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function CardStatus({ status }: { status?: CardConnectionResponse["status"] }) {
  const meta =
    status === "Active"
      ? { label: "Active", dot: "bg-emerald-500", text: "text-emerald-600" }
      : status === "Broken"
        ? { label: "Needs re-linking", dot: "bg-destructive", text: "text-destructive" }
        : status === "Pending"
          ? { label: "Linking", dot: "bg-amber-500", text: "text-amber-600" }
          : { label: status ?? "Unknown", dot: "bg-muted-foreground", text: "text-muted-foreground" };
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 text-xs font-medium", meta.text)}>
      <span className={cn("size-1.5 rounded-full", meta.dot)} />
      {meta.label}
    </span>
  );
}

function SetupStatus({ card }: { card: CardConnectionResponse }) {
  if (!card.clearingLedgerAccountId) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap text-amber-600">
        <TriangleAlert className="size-3.5" /> Needs a clearing account
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap text-muted-foreground">
      <CircleCheck className="size-3.5 text-emerald-600" /> Ready to post
    </span>
  );
}
